import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import Intercom from '@intercom/messenger-js-sdk';
import * as moment from 'moment';
import { Subject, Subscription, interval, merge } from 'rxjs';
import { startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { Location } from './interfaces/homepage';
import { User } from './models/user';
import { BookingStoreService } from './modules/booking-flow/booking-store.service';
import { ApiService } from './services/api.service';
import { AuthAppService } from './services/auth-app/auth-app.service';
import { Constants } from './services/constants';
import { MaintenanceService } from './services/maintenance.service';
import { StoreService } from './services/store.service';
import { SchemaService } from './shared/components/seo-extention/schema.service';
import { SeoExtentionComponent } from './shared/components/seo-extention/seo-extention.component';
import { CanonicalUrlPipe } from './shared/pipes/canonical-url.pipe';
import { SlugTextPipe } from './shared/pipes/slug-text.pipe';
import { SharedCompsService } from './shared/shared-comps.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends SeoExtentionComponent implements OnInit, OnDestroy {
  trinityLoggedUser: User;

  showFooter = true;
  showHeader = true;
  userEmail = '';
  name = 'TRINITY-USER';
  platform = 'TRINITY';
  hmacHash = '';
  previousUrl = '';
  currentUrl = '';
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthAppService,
    private api: ApiService,
    public store: StoreService,
    @Inject(PLATFORM_ID) private platformId: any,
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private dom_: any,
    private schema: SchemaService,
    private slugPipe: SlugTextPipe,
    private canonical_: CanonicalUrlPipe,
    private booking_store: BookingStoreService,
    private shared: SharedCompsService,
    private maintenanceService: MaintenanceService
  ) {
    super(router, route, titleService, metaService, dom_, schema, slugPipe, canonical_);
  }

  private destroy$ = new Subject<void>();
  private maintenanceStop$ = new Subject<void>();

  ngOnInit(): void {
    super.initialCall();

    if (isPlatformBrowser(this.platformId)) {
      this.handleMaintenanceMode();
      const fetchMeraLocationSub = this.store.fetchmeraLocation.subscribe(() => {
        this.getmeraLocation();
      });
      this.subscriptions.push(fetchMeraLocationSub);

      const meraLocationSub = this.store.meraLocationSub.subscribe((resp) => {
        if (!resp.post_code) {
          this.getmeraLocation();
        }
        const regex = new RegExp(/^(0[289][0-9]{2})|([1-9][0-9]{3})$/);
        if (resp.post_code && !regex.test(resp.post_code)) {
          resp.city_name = 'Sydney';
          resp.post_code = '2000';
          resp.lng = '151.2092955';
          resp.lat = '-33.8688197';
          resp.state_name = 'New South Wales';
          this.store.setmeraLocation(resp);
        }
      });
      this.subscriptions.push(meraLocationSub);

      this.getTrinityLoggedUser();
      const trinityLoggedUserSub = this.authService.trinityLoggedUser.subscribe({
        next: (user: any) => {
          if (user?.email) {
            this.userEmail = user.email || '';
            this.name = user.name || '';
          }
        }
      });
      this.subscriptions.push(trinityLoggedUserSub);

      if (this.userEmail) {
        this.hmacHash = this.store.generateHmac(this.userEmail, Constants.INTERCOM_CONFIG.secretKey);
      }
      // For anonymous users, no email or HMAC needed - Intercom automation will handle email collection
      this.inItIntercom();
      const routerEventsSub = this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          // Always fall back to empty strings
          this.previousUrl = this.currentUrl || '';
          this.currentUrl = event.urlAfterRedirects || '';

          // Guard against undefined before using .includes()
          const tempBookingSlug = this.getTemporarySlugForCancel();
          if (
            (this.previousUrl || '').includes('/checkout') &&
            !(this.currentUrl || '').includes('/confirm/') &&
            tempBookingSlug
          ) {
            this.api.cancelTempAppointment(tempBookingSlug).subscribe({
              next: () => this.shared.showSnackBar('Temporary appointment cancelled', 3),
              error: () => this.shared.showSnackBar('Failed to cancel temporary appointment', 3)
            });
          }

          setTimeout(() => {
            const contentContainer: any = document.querySelector('body') || window;
            // optional: use a valid options object
            contentContainer?.scrollIntoView?.({ block: 'start' });
          }, 300);

          // Use a safe URL string for splitting
          const safeUrl = event.urlAfterRedirects || event.url || '';
          const url_split = safeUrl.split('/');
          if (
            url_split[1] === 'search' ||
            url_split[1] === 'business' ||
            url_split[1] === 'wellbeing' ||
            url_split[1] === 'booking' ||
            url_split[1] === 'checkout' ||
            url_split[1] === 'confirm' ||
            url_split[1] === 'profile' ||
            url_split[1] === 'link' ||
            url_split[1] === 'maintenance'
          ) {
            this.showFooter = false;
          } else {
            this.showFooter = true;
          }
          this.showHeader = !(
            url_split[1] === 'booking' ||
            url_split[1] === 'checkout' ||
            url_split[1] === 'confirm' ||
            url_split[1] === 'maintenance'
          );
        }
      });
      this.subscriptions.push(routerEventsSub);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
    this.maintenanceStop$.next();
    this.maintenanceStop$.complete();
  }

  private isValidTemporarySlug(slug: string | undefined | null): boolean {
    return !!slug && slug !== 'undefined' && slug !== 'null';
  }

  private getTemporarySlugForCancel(): string {
    const storeSlug = this.booking_store.bookingTemporarySlug;
    if (this.isValidTemporarySlug(storeSlug)) {
      return storeSlug as string;
    }

    const previousPath = (this.previousUrl || '').split('?')[0];
    const match = previousPath.match(/^\/booking\/([^/]+)\/checkout(?:\/)?$/);
    return match?.[1] && this.isValidTemporarySlug(match[1]) ? match[1] : '';
  }

  inItIntercom() {
    if (this.userEmail) {
      // Logged-in user with email and HMAC
      Intercom({
        app_id: Constants.INTERCOM_CONFIG.appId,
        user_name: this.name,
        email: this.userEmail,
        platform: this.platform,
        user_hash: this.hmacHash
      });
    } else {
      // Anonymous user - no email, let Intercom automation ask for it
      Intercom({
        app_id: Constants.INTERCOM_CONFIG.appId,
        user_name: `Anonymous ${this.platform} User`,
        platform: this.platform
      });
    }
  }

  getTrinityLoggedUser() {
    try {
      const lastLogin = localStorage.getItem('trinityLastLogin');
      if (lastLogin) {
        // Calculate the difference in hours
        const differenceInHours = moment().diff(moment(new Date(lastLogin)), 'hours');
        if (differenceInHours >= 24) {
          this.authService.purgeAuth();
        } else {
          this.fetchUserData();
        }
      } else {
        this.fetchUserData();
      }
    } catch (error) {
      console.info('local storage error: IGNORE');
    }
  }

  fetchUserData() {
    const user = localStorage.getItem('trinityLoggedUser');
    if (!user) return;
    this.authService.setTrinityLoggedUser(JSON.parse(user));
  }

  getmeraLocation() {
    // Check if geolocation is supported by the browser
    if (isPlatformBrowser(this.platformId) && 'geolocation' in window.navigator) {
      // Prompt user for permission to access their location
      window.navigator.geolocation.getCurrentPosition(
        // Success callback function
        (position) => {
          // Get the user's latitude and longitude coordinates
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          // const lat = '-37.8156954';
          // const lng = '144.8828322';

          this.getCoordinates(lat, lng);

          // Do something with the location data, e.g. display on a map
        },
        // Error callback function
        (error) => {
          // Handle errors, e.g. user denied location sharing permissions
          console.error('Error getting user location:', error);
        }
      );
    } else {
      // Geolocation is not supported by the browser
      console.error('Geolocation is not supported by this browser.');
      const lat = '-33.8688197';
      const lng = '151.2092955';

      this.getCoordinates(lat, lng);
    }
  }

  getCoordinates(latitude: string | number, longitude: string | number) {
    const lat_lon = latitude + ', ' + longitude;
    this.api.getGeoCodingByAddress(lat_lon).subscribe({
      next: (response) => {
        let post_code = '2000';
        let state_name = 'New South Wales';
        let city_name = 'Sydeney';
        const list_with_post_code = response.geocode_details.results.find(
          (geocode: any) => geocode.address_components.findIndex((x: any) => x.types.indexOf('postal_code') > -1) > -1
        );

        let addresscomponents = [];
        if (list_with_post_code && list_with_post_code.address_components) {
          addresscomponents = [...list_with_post_code.address_components];
        } else {
          addresscomponents = [...response.geocode_details.results[0].address_components];
        }

        for (let i = 0; i < addresscomponents.length; i++) {
          const component = addresscomponents[i];
          if (component.types.indexOf('postal_code') > -1) {
            post_code = component.long_name;
          } else if (component.types.indexOf('administrative_area_level_1') > -1) {
            state_name = component.long_name;
          } else if (
            component.types.indexOf('locality') > -1 ||
            component.types.indexOf('sublocality') > -1 ||
            component.types.indexOf('neighborhood') > -1
          ) {
            city_name = component.long_name;
          }
        }
        const location = new Location();
        location.lat = latitude;
        location.lng = longitude;
        location.post_code = post_code;
        location.city_name = city_name;
        location.state_name = state_name;
        this.store.setmeraLocation(location);
      }
    });
  }

  private handleMaintenanceMode() {
    interval(60000)
      .pipe(
        startWith(0),
        switchMap(() => this.maintenanceService.getMaintenanceConfig().pipe(take(1))),
        takeUntil(merge(this.destroy$, this.maintenanceStop$))
      )
      .subscribe((config) => {
        this.store.maintenanceConfig = config;
        if (!config.repeat) {
          this.maintenanceStop$.next();
        }
        const isActive = this.store.isMaintenanceActive(config.maintenance);
        const alreadyOnMaintenance = this.router.url.startsWith('/maintenance');

        if (isActive && !alreadyOnMaintenance) {
          this.router.navigate(['/maintenance']);
        } else if (!isActive && alreadyOnMaintenance) {
          this.router.navigate(['/']);
        }
      });
  }
}
